import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { NotifyOrConfirmType } from '../enums/notify-or-confirm-type.enum';
import { UserRole } from '../enums/user-role.enum';
import { WorkflowToken } from '../enums/workflow-token.enum';
import { AuthService } from '../services/auth.service';
import { NotifyOrConfirmService } from '../services/notify-or-confirm.service';
import { SessionService } from '../services/session.service';

@Injectable()
export class AuthenticatedGuard implements CanActivate {
	constructor(
		private authService: AuthService,
		private sessionService: SessionService,
		private router: Router,
		private notifyOrConfirmService: NotifyOrConfirmService,
		private translate: TranslateService
	) {}

	/**
	 * Can current user activates the page
	 *
	 * @param route - Active rout
	 * @param state - Router state
	 * @param allowedRoles - Array with names of allowed roles, if set at least one is necessary
	 * @param allowedWorkflows - Array with name of allowed workflows, if set at least one is necessary
	 * @return
	 */
	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
		allowedRoles?: UserRole[],
		allowedWorkflows?: WorkflowToken[]
	): Observable<boolean> {
		const redirectUrl = route.data['guardRedirectUrl'];
		return this.guardHandling(redirectUrl, route.data['guardAllowedRoles'], route.data['guardAllowedWorkflows']);
	}

	/**
	 * Guard handling
	 *
	 * @param redirectUrl
	 * @param allowedRoles - Array with names of allowed roles, if set at least one is necessary
	 * @param allowedWorkflows - Array with names of allowed workflows, if set at least one is necessary
	 * @returns
	 */
	private guardHandling(redirectUrl?: string, allowedRoles?: UserRole[], allowedWorkflows?: WorkflowToken[]): Observable<boolean> {
		// Return Observable with the logged in status
		return new Observable<boolean>((subscriber) => {
			// Check if user logged in
			this.authService.loggedIn(true, false).subscribe((loggedIn: boolean) => {
				if (loggedIn) {
					const user = this.sessionService.currentUser;

					// Check if user is authorized:
					// The user has at least one of the configured user roles and one of the configured workflow token.
					// If the roles or workflows do not exist or are empty, they are not checked and are considered valid.
					if (!user.isAuthorized({ roles: allowedRoles, workflows: allowedWorkflows })) {
						if (redirectUrl) {
							this.router.navigate([redirectUrl]);
						} else {
							this.showUnauthorizedModalView();
						}

						// User is NOT authorized
						subscriber.next(false);
						subscriber.complete();
						return false;
					}

					// User is authorized
					subscriber.next(true);
					subscriber.complete();
					return true;
				} else if (redirectUrl) {
					this.router.navigate([redirectUrl]);
				} else {
					this.router.navigate(['/']);
				}

				// User is NOT authorized
				subscriber.next(false);
				subscriber.complete();
				return false;
			});
		});
	}

	/**
	 * Shows a modal view with permission denied message
	 */
	private showUnauthorizedModalView() {
		const title = this.translate.instant('authenticatedGuard.error.oops');
		const message = this.translate.instant('authenticatedGuard.error.notAuthorizedMessage');
		const buttonText = this.translate.instant('authenticatedGuard.button.login');

		if (this.router.url === '/') {
			// Open notification if the user set the URL manually
			this.notifyOrConfirmService.showNotification(title, message, NotifyOrConfirmType.ERROR, buttonText);
			this.notifyOrConfirmService.modalValueObserver.subscribe(() => {
				this.authService.logout().subscribe();
			});
		} else {
			// Open confirmation if the user is using a router link
			this.notifyOrConfirmService.showConfirmation(title, message, {
				confirmButtonText: buttonText,
				cancelButtonText: this.translate.instant('authenticatedGuard.button.ok')
			});
			this.notifyOrConfirmService.modalValueObserver.subscribe((confirmed) => {
				this.authService.logout().subscribe();
			});
		}
	}
}
